import { useEffect, useMemo, useState } from "react";
import {
  ordersList,
  integrationsList,
} from "../../../../../api/auth";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
  Cell,
} from "recharts";
import {
  RefreshCw,
  Search,
  Layers3,
  CheckCircle2,
  Clock3,
  XCircle,
  ShoppingBag,
} from "lucide-react";
import Loading from "../../../../ui/Loading";

const COLORS = {
  primary: "#2563EB",
  success: "#16A34A",
  warning: "#D97706",
  danger: "#DC2626",
  slate: "#64748B",
  grid: "#E2E8F0",
};

export default function OrdersDashboard() {
  const [orders, setOrders] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedIntegration, setSelectedIntegration] = useState("");
  const [error, setError] = useState("");

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const [ordersRes, integrationsRes] = await Promise.all([
        ordersList(),
        integrationsList(),
      ]);

      setOrders(
        Array.isArray(ordersRes?.data)
          ? ordersRes.data
          : Array.isArray(ordersRes)
          ? ordersRes
          : []
      );

      setIntegrations(
        Array.isArray(integrationsRes?.data)
          ? integrationsRes.data
          : Array.isArray(integrationsRes)
          ? integrationsRes
          : []
      );
    } catch (err) {
      setError("Failed to load dashboard data.");
      setOrders([]);
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    const q = search.toLowerCase().trim();

    return orders.filter((o) => {
      const matchSearch =
        !q ||
        String(o.client_name || "").toLowerCase().includes(q) ||
        String(o.client_phone || "").includes(q) ||
        String(o.produit || "").toLowerCase().includes(q);

      const matchIntegration = selectedIntegration
        ? o.platform === selectedIntegration
        : true;

      return matchSearch && matchIntegration;
    });
  }, [orders, search, selectedIntegration]);

  const stats = useMemo(() => {
    const total = filteredOrders.length;
    const confirmed = filteredOrders.filter(
      (o) => String(o.local_status || "").toLowerCase() === "confirmed"
    ).length;

    const cancelled = filteredOrders.filter((o) =>
      ["cancelled", "cancelled_by_client", "cancelled_by_admin", "rejected"].includes(
        String(o.local_status || "").toLowerCase()
      )
    ).length;

    const pending = filteredOrders.filter((o) =>
      ["pending", "new", "pending_confirmation", "no_answer", "call_back_later"].includes(
        String(o.local_status || "").toLowerCase()
      )
    ).length;

    const revenue = filteredOrders.reduce(
      (sum, o) => sum + Number(o.total_amount || 0),
      0
    );

    return {
      total,
      confirmed,
      cancelled,
      pending,
      revenue,
    };
  }, [filteredOrders]);

  const statusChartData = useMemo(() => {
    return [
      { name: "Pending", value: stats.pending, fill: COLORS.warning },
      { name: "Confirmed", value: stats.confirmed, fill: COLORS.success },
      { name: "Cancelled", value: stats.cancelled, fill: COLORS.danger },
    ];
  }, [stats]);

  const trendChartData = useMemo(() => {
    const grouped = {};

    filteredOrders.forEach((o) => {
      const date = o.created_at
        ? new Date(o.created_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          })
        : "Unknown";

      grouped[date] = (grouped[date] || 0) + 1;
    });

    return Object.entries(grouped)
      .map(([name, orders]) => ({ name, orders }))
      .slice(-8);
  }, [filteredOrders]);

  const integrationChartData = useMemo(() => {
    const grouped = {};

    filteredOrders.forEach((o) => {
      const platform = o.platform || "Manual";
      grouped[platform] = (grouped[platform] || 0) + 1;
    });

    return Object.entries(grouped)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [filteredOrders]);

  const recentOrders = useMemo(() => {
    return [...filteredOrders]
      .sort((a, b) => {
        const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      })
      .slice(0, 8);
  }, [filteredOrders]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <Loading fullScreen text="Loading orders dashboard..." variant="dots" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-3 py-4 sm:px-4 md:px-6">
      <div className="mx-auto max-w-7xl space-y-4 sm:space-y-6">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm sm:rounded-3xl sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 dark:bg-indigo-500/10 px-3 py-1 text-[11px] font-semibold text-indigo-700 dark:text-indigo-400 sm:text-xs">
                <Layers3 className="h-3.5 w-3.5" />
                Orders Overview
              </div>

              <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
                Orders Dashboard
              </h1>

              <p className="mt-2 text-sm text-slate-500 dark:text-slate-500">
                Track orders performance, statuses, revenue, and integrations.
              </p>
            </div>

            <button
              onClick={loadOrders}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 dark:bg-slate-700 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 dark:hover:bg-slate-600 sm:w-auto"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-400">
            {error}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm sm:rounded-3xl sm:p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_280px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                placeholder="Search by client, phone, product..."
                className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-3 pl-11 pr-4 text-sm text-slate-700 dark:text-slate-300 outline-none transition focus:border-slate-300 dark:focus:border-slate-600 focus:ring-4 focus:ring-slate-100 dark:focus:ring-slate-800"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-700 dark:text-slate-300 outline-none transition focus:border-slate-300 dark:focus:border-slate-600 focus:ring-4 focus:ring-slate-100 dark:focus:ring-slate-800"
              value={selectedIntegration}
              onChange={(e) => setSelectedIntegration(e.target.value)}
            >
              <option value="">All Integrations</option>
              {integrations.map((i) => (
                <option key={i.id} value={i.platform}>
                  {i.store_name || i.platform}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            title="Total Orders"
            value={stats.total}
            subtitle="Filtered results"
            icon={ShoppingBag}
            iconColor="text-indigo-600 dark:text-indigo-400"
            iconBg="bg-indigo-50 dark:bg-indigo-500/10"
          />
          <KpiCard
            title="Confirmed"
            value={stats.confirmed}
            subtitle="Validated orders"
            icon={CheckCircle2}
            iconColor="text-green-600 dark:text-emerald-400"
            iconBg="bg-green-50 dark:bg-emerald-500/10"
          />
          <KpiCard
            title="Pending"
            value={stats.pending}
            subtitle="Awaiting action"
            icon={Clock3}
            iconColor="text-amber-600 dark:text-amber-400"
            iconBg="bg-amber-50 dark:bg-amber-500/10"
          />
          <KpiCard
            title="Cancelled"
            value={stats.cancelled}
            subtitle="Stopped orders"
            icon={XCircle}
            iconColor="text-red-600 dark:text-rose-400"
            iconBg="bg-red-50 dark:bg-rose-500/10"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <ChartCard
            title="Orders Trend"
            subtitle="Recent creation activity"
            className="xl:col-span-2"
          >
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trendChartData}>
                <defs>
                  <linearGradient id="ordersTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="orders"
                  stroke={COLORS.primary}
                  strokeWidth={3}
                  fill="url(#ordersTrendFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Orders by Status"
            subtitle="Current filtered distribution"
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={statusChartData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip />
                <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                  {statusChartData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <ChartCard
            title="Top Integrations"
            subtitle="Highest order volume"
            className="xl:col-span-2"
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={integrationChartData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip />
                <Bar dataKey="value" fill={COLORS.primary} radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm sm:rounded-3xl sm:p-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Quick Summary</h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-500">
                Small snapshot for fast reading.
              </p>
            </div>

            <div className="space-y-3">
              <MiniStat
                label="Top integration"
                value={integrationChartData[0]?.name || "—"}
                color="bg-indigo-500"
              />
              <MiniStat
                label="Revenue"
                value={`${stats.revenue.toFixed(2)} MAD`}
                color="bg-green-500"
              />
              <MiniStat
                label="Filtered orders"
                value={String(filteredOrders.length)}
                color="bg-amber-500"
              />
              <MiniStat
                label="Integrations"
                value={String(integrations.length)}
                color="bg-sky-500"
              />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm sm:rounded-3xl">
          <div className="flex flex-col gap-3 border-b border-slate-200 dark:border-slate-800 px-4 py-4 sm:px-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Recent Orders</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-500">
                Latest filtered orders overview.
              </p>
            </div>

            <div className="rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-600 dark:text-slate-400">
              {recentOrders.length} shown
            </div>
          </div>

          {!recentOrders.length ? (
            <div className="px-6 py-14 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800">
                <ShoppingBag className="h-6 w-6 text-slate-500 dark:text-slate-500" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
                No orders found
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-500">
                Try changing filters or search.
              </p>
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto lg:block">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/60">
                    <tr className="border-b border-slate-200 dark:border-slate-800">
                      <th className="px-5 py-4 text-left font-semibold text-slate-500 dark:text-slate-500">Client</th>
                      <th className="px-5 py-4 text-left font-semibold text-slate-500 dark:text-slate-500">Phone</th>
                      <th className="px-5 py-4 text-left font-semibold text-slate-500 dark:text-slate-500">Product</th>
                      <th className="px-5 py-4 text-left font-semibold text-slate-500 dark:text-slate-500">Status</th>
                      <th className="px-5 py-4 text-left font-semibold text-slate-500 dark:text-slate-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((o) => (
                      <tr
                        key={o.id}
                        className="border-b border-slate-100 dark:border-slate-800 transition hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <td className="px-5 py-4 font-medium text-slate-900 dark:text-slate-100">
                          {o.client_name || "—"}
                        </td>
                        <td className="px-5 py-4 text-slate-600 dark:text-slate-400">
                          {o.client_phone || "—"}
                        </td>
                        <td className="px-5 py-4 text-slate-600 dark:text-slate-400">
                          {o.produit || "—"}
                        </td>
                        <td className="px-5 py-4">
                          <StatusBadge status={o.local_status} />
                        </td>
                        <td className="px-5 py-4 font-semibold text-slate-900 dark:text-slate-100">
                          {Number(o.total_amount || 0).toFixed(2)} MAD
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-3 p-3 lg:hidden">
                {recentOrders.map((o) => (
                  <div
                    key={o.id}
                    className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-900 dark:text-slate-100">
                          {o.client_name || "—"}
                        </div>
                        <div className="mt-1 text-sm text-slate-500 dark:text-slate-500">
                          {o.client_phone || "—"}
                        </div>
                      </div>
                      <StatusBadge status={o.local_status} />
                    </div>

                    <div className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <div>{o.produit || "—"}</div>
                      <div className="font-semibold text-slate-900 dark:text-slate-100">
                        {Number(o.total_amount || 0).toFixed(2)} MAD
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ title, value, subtitle, icon: Icon, iconColor, iconBg }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm sm:rounded-3xl sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-500">{title}</p>
          <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-2xl">
            {value}
          </h2>
          <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>
        </div>

        <div className={`shrink-0 rounded-2xl p-3 ${iconBg}`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm sm:rounded-3xl sm:p-5 ${className}`}>
      <div className="mb-4">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 sm:text-lg">{title}</h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-slate-200 dark:border-slate-800 px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${color}`} />
        <span className="truncate text-sm text-slate-500 dark:text-slate-500">{label}</span>
      </div>
      <span className="ml-3 text-right text-sm font-semibold text-slate-900 dark:text-slate-100">
        {value}
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const value = String(status || "").toLowerCase();

  let classes = "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300";

  if (["confirmed"].includes(value)) {
    classes = "bg-green-100 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400";
  } else if (
    ["cancelled", "cancelled_by_client", "cancelled_by_admin", "rejected"].includes(value)
  ) {
    classes = "bg-red-100 dark:bg-rose-500/10 text-red-700 dark:text-rose-400";
  } else if (
    ["pending", "new", "pending_confirmation", "no_answer", "call_back_later"].includes(value)
  ) {
    classes = "bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400";
  }

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${classes}`}>
      {status || "unknown"}
    </span>
  );
}